import React, { useEffect, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { motion } from 'framer-motion';

import type { MegaNavItem } from '../types';

interface MegaNavFlyoutProps {
  item: MegaNavItem;
  isVisible: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  topOffset: number;
}

const flyoutVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
};

const MegaNavFlyout: React.FC<MegaNavFlyoutProps> = ({
  item,
  isVisible,
  onMouseEnter,
  onMouseLeave,
  topOffset,
}) => {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  // Keep the panel rendered (just transparent) until the close animation
  // finishes, so the exit transition is actually visible. Starts hidden so the
  // server render and first paint match (and stays in the DOM for SEO).
  const [isFullyHidden, setIsFullyHidden] = useState(true);

  useEffect(() => {
    if (isVisible) {
      // Reveal immediately on open so the enter animation can play.
      setIsFullyHidden(false);
    } else {
      setIsAnimationComplete(false);
    }
  }, [isVisible]);

  const handleAnimationComplete = (definition: string) => {
    if (definition === 'visible') {
      setIsAnimationComplete(true);
    } else if (definition === 'hidden') {
      // Apply `visibility: hidden` only after the close animation completes.
      setIsFullyHidden(true);
    }
  };

  // Only enable mouse events after the open animation completes
  const enableMouseEvents = isVisible && isAnimationComplete;

  return (
    <motion.div
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      variants={flyoutVariants}
      onAnimationComplete={handleAnimationComplete}
      onMouseEnter={enableMouseEvents ? onMouseEnter : undefined}
      onMouseLeave={enableMouseEvents ? onMouseLeave : undefined}
      // top is computed at runtime from the nav's position so the flyout sits
      // flush under the header regardless of any banner above it.
      style={{ top: topOffset }}
      // Always render in HTML for SEO, but control visibility with CSS. The
      // panel stays `visible` through the close animation and only flips to
      // `invisible` once it finishes (see handleAnimationComplete).
      className={cn(
        'fixed inset-x-0 z-[1000] bg-od-bg-primary py-8 shadow-md',
        isFullyHidden ? 'invisible' : 'visible',
        isVisible ? 'pointer-events-auto' : 'pointer-events-none',
      )}
    >
      <Box className="w-full px-5.5">
        <Row className="w-full justify-around gap-3">
          {item.sections?.map((section, sectionIndex) => (
            <Col key={sectionIndex} className="gap-2">
              <Text as="p" className="text-body-md font-medium">
                {section.title}
              </Text>
              <Col className="gap-1.5">
                {section.links.map((link, linkIndex) => (
                  <Box
                    key={linkIndex}
                    href={link.href}
                    aria-label={`Navigate to ${link.name}`}
                    analyticsName={`cosmos-mega-nav-${item.name.toLowerCase()}-${link.name
                      .toLowerCase()
                      .replace(/\s+/g, '-')}`}
                    className="block text-od-content-primary no-underline hover:text-od-content-hover-accent-brand"
                  >
                    <Text as="span" className="text-body-md text-inherit">
                      {link.name}
                    </Text>
                  </Box>
                ))}
              </Col>
            </Col>
          ))}
        </Row>
      </Box>
    </motion.div>
  );
};

export default MegaNavFlyout;
